import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import pluralize from "pluralize";
import React, { useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Event } from "types";
import { api } from "@/api/api";
import { NotifyCustomerCheckbox } from "@/components/Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getRemoveTargets, RemoveTarget } from "@/models/Event";

interface RemoveExperienceOptionModalProps {
    events: Pick<Event, "id" | "options">[];
    isOpen: boolean;
    onClose: () => void;
}

export const RemoveExperienceOptionModal = ({ events, isOpen, onClose }: RemoveExperienceOptionModalProps) => {
    const isRemovingRef = useRef(false);
    const targets = getRemoveTargets(events);

    if (targets.length === 0) {
        return null;
    }

    const handleClose = () => {
        if (isRemovingRef.current) {
            return;
        }
        onClose();
    };

    return (
        <Modal shouldCloseOnOutsideClick={false} isOpen={isOpen} onClose={handleClose}>
            <ModalContent targets={targets} isRemovingRef={isRemovingRef} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({
    targets,
    onClose,
    isRemovingRef,
}: {
    targets: RemoveTarget[];
    onClose: () => void;
    isRemovingRef: React.MutableRefObject<boolean>;
}) => {
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const [shouldNotifyCustomer, setShouldNotifyCustomer] = useState(true);
    const handleNotifyChange = useMemoizedFn((event: React.ChangeEvent<HTMLInputElement>) =>
        setShouldNotifyCustomer(event.target.checked),
    );

    // Settled rather than all: one failed event must not leave the others un-invalidated.
    const { mutateAsync: remove, isLoading } = useMutation(async () => {
        const results = await Promise.allSettled(
            targets.map(async ({ eventId, eventOptionId }) =>
                api.delete(`/events/${eventId}/options/${eventOptionId}`, {
                    data: { _emailOverrides: { sendEmailToTraveler: shouldNotifyCustomer } },
                }),
            ),
        );
        return results.filter((result) => result.status === "rejected").length;
    });

    const handleRemove = async () => {
        isRemovingRef.current = true;
        try {
            const failedCount = await remove();
            invalidate("events");
            for (const { eventId } of targets) {
                queryClient.invalidateQueries(["events", eventId]);
            }
            invalidate("timeslots");
            if (failedCount > 0) {
                flash.danger(
                    `Could not remove the Experience Option from ${failedCount} ${pluralize("event", failedCount)}`,
                );
                return;
            }
            flash.success(
                `${pluralize("Experience Option", targets.length)} removed from ${targets.length} ${pluralize(
                    "event",
                    targets.length,
                )}`,
            );
            onClose();
        } finally {
            isRemovingRef.current = false;
        }
    };

    return (
        <>
            <Modal.Header>Remove Experience Option</Modal.Header>
            <Modal.Body>
                <p data-testid="remove-experience-option-message">
                    {targets.length === 1 ? (
                        "This Experience Option will be removed from this event."
                    ) : (
                        <>
                            Each event&apos;s Experience Option will be removed from the{" "}
                            <var data-var="eventCount">{targets.length} selected events</var>.
                        </>
                    )}
                </p>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-between">
                    <NotifyCustomerCheckbox isChecked={shouldNotifyCustomer} onChange={handleNotifyChange} />
                    <div className="flex items-center space-x-2">
                        <SecondaryButton
                            data-testid="remove-experience-option-cancel-button"
                            disabled={isLoading}
                            onClick={onClose}
                        >
                            Cancel
                        </SecondaryButton>
                        <SubmitButton
                            data-testid="remove-experience-option-submit-button"
                            isLoading={isLoading}
                            onClick={handleRemove}
                        >
                            Remove
                        </SubmitButton>
                    </div>
                </div>
            </Modal.Footer>
        </>
    );
};
